import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import '../../css/AdminServiceCompanyEdit.css';
import {useNavigate, useParams} from "react-router-dom";
import {AdminServiceCompanyRelative} from "../components/AdminServiceCompanyRelative";

function AdminServiceCompanyEdit() {
    const navigate = useNavigate();
    const maxTrades = 4;
    const { id } = useParams();
    const [statuses, setStatuses] = useState([]);
    const [cities, setCities] = useState([]);
    const [trades, setTrades] = useState([]);
    const [tradesAtLimit, setTradesAtLimit] = useState(false);
    const [company, setCompany] = useState({
        link_hash: uuidv4().slice(0, 8),
        create_password_hash: uuidv4().slice(0, 8),
        trades: [],
        cities: [],
        status: "Internal Incomplete"
    });
    const [originalCompany, setOriginalCompany] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [buttonState, setButtonState] = useState('No Changes');
    const [error, setError] = useState('');
    const baseUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;

    useEffect(() => {
        axios.get('/api/cities')
            .then(response => setCities(response.data))
            .catch(error => setError('Error fetching cities: ' + error));
        axios.get('/api/trades')
            .then(response => setTrades(response.data))
            .catch(error => setError('Error fetching trades: ' + error));
        axios.get('/api/admin/service_company_statuses')
            .then(response => setStatuses(response.data.statuses))
            .catch(error => setError('Error fetching statuses: ' + error));
    }, []);


    useEffect(() => {
        fetchCompany();
    }, [id]);

    const fetchCompany = async () => {
        if (id === 'new') return;
        try {
            const response = await axios.get(`/api/admin/service_company?id=${id}`);
            setCompany(response.data);
            setOriginalCompany(response.data);
            if (response.data.trades.length >= maxTrades) {
                setTradesAtLimit(true);
            }
        } catch (error) {
            console.error('Error fetching company:', error);
        }
    };

    const getTradeIndex = (tradeId) => {
        if (!Array.isArray(company?.trades) || company.trades.length === 0) return -1;
        return company.trades.findIndex((trade) => trade && String(trade.id) === String(tradeId));
    };

    const handleInputChange = (field, value) => {
        const updatedCompany = { ...company, [field]: value };
        setCompany(updatedCompany);

        if (JSON.stringify(updatedCompany) === JSON.stringify(originalCompany)) {
            setButtonState('No Changes');
        } else {
            setButtonState('Save Changes');
        }
    };

    const handlePrimaryTradeChange = (tradeId) => {
        const updatedCompany = (() => {
            const existingTrades = [...(company.trades || [])];

            if (existingTrades.length > 0 && String(existingTrades[0]?.id) === String(tradeId)) {
                return company;
            }

            const selectedTrade = trades.find((trade) => String(trade.id) === String(tradeId));
            if (!selectedTrade) return company;

            let filteredTrades = existingTrades.filter((trade) => String(trade.id) !== String(tradeId));
            filteredTrades[0] = selectedTrade;

            return {
                ...company,
                trades: filteredTrades,
            };
        })();
        setCompany(updatedCompany);

        if (JSON.stringify(updatedCompany) === JSON.stringify(originalCompany)) {
            setButtonState('No Changes');
        } else {
            setButtonState('Save Changes');
        }
    };

    const handleTradeCheckboxChange = (tradeId, isChecked) => {
        const updatedCompany = (() => {
            const prevCompany = company;

            const selectedTrade = trades.find((trade) => String(trade.id) === String(tradeId));
            if (!selectedTrade) return company;

            const updatedTrades = isChecked
                ? [...(prevCompany.trades || []), selectedTrade]
                : prevCompany.trades.filter((trade) => String(trade.id) !== String(tradeId));

            return {
                ...prevCompany,
                trades: updatedTrades,
            };
        })();
        setCompany(updatedCompany);
        if (updatedCompany.trades.length >= maxTrades) {
            setTradesAtLimit(true);
        } else {
            setTradesAtLimit(false);
        }

        if (JSON.stringify(updatedCompany) === JSON.stringify(originalCompany)) {
            setButtonState('No Changes');
        } else {
            setButtonState('Save Changes');
        }
    };

    const handleCityCheckboxChange = (cityId, isChecked) => {
        const updatedCompany = (() => {
            const prevCompany = company;

            const selectedCity = cities.find((city) => String(city.id) === String(cityId));
            if (!selectedCity) return company;

            const updatedCities = isChecked
                ? [...(prevCompany.cities || []), selectedCity]
                : prevCompany.cities.filter((city) => String(city.id) !== String(cityId));

            return {
                ...prevCompany,
                cities: updatedCities,
            };
        })();
        setCompany(updatedCompany);

        if (JSON.stringify(updatedCompany) === JSON.stringify(originalCompany)) {
            setButtonState('No Changes');
        } else {
            setButtonState('Save Changes');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (buttonState !== 'Save Changes') return;

        if (company.status === "Active/Verified") {
            const userConfirmed = window.confirm("By Activating this Service Provider, all other copies of this Service Provider will be Archived. Are you sure you want to proceed?");
            if (!userConfirmed) {
                return;
            }
        }

        setButtonState('Saving');
        setIsSubmitting(true);

        try {
            if (id === "new") {
                const response = await axios.post("/api/admin/service_company", {company: company});
                if (response.status !== 200) {
                    alert("Error creating company");
                }
            } else {
                const response = await axios.put("/api/admin/service_company", {company: company});
                if (response.status !== 200) {
                    alert("Error creating company");
                }
            }
            setOriginalCompany(company);
            setButtonState('No Changes');
            navigate("/admin/service-companies");
        } catch (error) {
            console.error('Error updating company:', error);
        } finally {
            setIsSubmitting(false);
            fetchCompany();
        }
    };

    return (
        <div className="adminServiceCompanyEditContainer">
            <div className="adminServiceCompanyEditNav"><a href="/admin/service-companies">&lt;&lt; Back to
                Dashboard</a></div>
            <h2>Edit Service Company</h2>
            <h4>Last Edited By: {company?.edited_by?.email || "Unknown User"}</h4>
            <h4>Profile Page: <a href={`/service-company/${company?.link_hash}`}>{`${baseUrl}/service-company/${company?.link_hash}`}</a></h4>
            {error && <div className="adminServiceCompanyEditError">{error}</div>}
            <form className="adminServiceCompanyEditForm" onSubmit={handleSubmit}>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Name:</label>
                    <input
                        type="text"
                        value={company?.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>License Number:</label>
                    <input
                        type="text"
                        value={company?.license_number || ''}
                        onChange={(e) => handleInputChange('license_number', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Phone:</label>
                    <input
                        type="text"
                        value={company?.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Connected Agents:</label>
                    <input
                        type="number"
                        value={company?.connected_agents || 0}
                        onChange={(e) => handleInputChange('connected_agents', e.target.value)}
                        required
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Service Records:</label>
                    <input
                        type="number"
                        value={company?.service_records || 0}
                        onChange={(e) => handleInputChange('service_records', e.target.value)}
                        required
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Recurring Agents Records:</label>
                    <input
                        type="number"
                        value={company?.recurring_agents_records || 0}
                        onChange={(e) => handleInputChange('recurring_agents_records', e.target.value)}
                        required
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Image URL:</label>
                    <input
                        type="text"
                        value={company?.image || ''}
                        onChange={(e) => handleInputChange('image', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Contact Name:</label>
                    <input
                        type="text"
                        value={company?.contact_name || ''}
                        onChange={(e) => handleInputChange('contact_name', e.target.value)}
                        required
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Email:</label>
                    <input
                        type="email"
                        value={company?.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Status:</label>
                    <select
                        value={company?.status || ''}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                        {statuses.map((status) => (
                            <option value={status.name}>{status.name}</option>
                        ))}
                    </select>
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Yelp Rating:</label>
                    <input
                        type="number"
                        step="0.1"
                        value={company?.yelp_rating || ''}
                        onChange={(e) => handleInputChange('yelp_rating', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Yelp Count:</label>
                    <input
                        type="number"
                        value={company?.yelp_count || ''}
                        onChange={(e) => handleInputChange('yelp_count', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Yelp URL:</label>
                    <input
                        type="url"
                        value={company?.yelp_url || ''}
                        onChange={(e) => handleInputChange('yelp_url', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Google Rating:</label>
                    <input
                        type="number"
                        step="0.1"
                        value={company?.google_rating || ''}
                        onChange={(e) => handleInputChange('google_rating', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Google Count:</label>
                    <input
                        type="number"
                        value={company?.google_count || ''}
                        onChange={(e) => handleInputChange('google_count', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Google URL:</label>
                    <input
                        type="url"
                        value={company?.google_url || ''}
                        onChange={(e) => handleInputChange('google_url', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Home Advisor URL:</label>
                    <input
                        type="url"
                        value={company?.home_advisor_url || ''}
                        onChange={(e) => handleInputChange('home_advisor_url', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Thumbtack URL:</label>
                    <input
                        type="url"
                        value={company?.thumbtack_url || ''}
                        onChange={(e) => handleInputChange('thumbtack_url', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Task Rabbit URL:</label>
                    <input
                        type="url"
                        value={company?.task_rabbit_url || ''}
                        onChange={(e) => handleInputChange('task_rabbit_url', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Other URL:</label>
                    <input
                        type="url"
                        value={company?.other_url || ''}
                        onChange={(e) => handleInputChange('other_url', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Homepage URL:</label>
                    <input
                        type="url"
                        value={company?.homepage_url || ''}
                        onChange={(e) => handleInputChange('homepage_url', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Address 1:</label>
                    <input
                        type="text"
                        value={company?.address_1 || ''}
                        onChange={(e) => handleInputChange('address_1', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Address 2:</label>
                    <input
                        type="text"
                        value={company?.address_2 || ''}
                        onChange={(e) => handleInputChange('address_2', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>City:</label>
                    <input
                        type="text"
                        value={company?.city || ''}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>State:</label>
                    <input
                        type="text"
                        value={company?.state || ''}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>ZIP:</label>
                    <input
                        type="text"
                        value={company?.zip || ''}
                        onChange={(e) => handleInputChange('zip', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Recent Realtor Email:</label>
                    <input
                        type="email"
                        value={company?.recent_realtor_email || ''}
                        onChange={(e) => handleInputChange('recent_realtor_email', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Recent Project Site:</label>
                    <input
                        type="text"
                        value={company?.recent_project_site || ''}
                        onChange={(e) => handleInputChange('recent_project_site', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Link Hash:</label>
                    <input
                        type="text"
                        value={company?.link_hash || ''}
                        onChange={(e) => handleInputChange('link_hash', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Create Password Hash:</label>
                    <input
                        type="text"
                        value={company?.create_password_hash || ''}
                        onChange={(e) => handleInputChange('create_password_hash', e.target.value)}
                    />
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Primary Trade:</label>
                    <select
                        value={company?.trades?.[0]?.id || ''}
                        onChange={(e) => handlePrimaryTradeChange(e.target.value)}
                    >
                        <option value="" disabled>
                            Please select the Primary Trade
                        </option>
                        {trades.map((trade) => (
                            <option key={trade.id} value={trade.id}>
                                {trade.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Other Trades:</label>
                    {trades?.length > 0 ? (
                        trades.map((trade, index) => (
                            <div key={trade.id}>
                                <label>
                                    <input
                                        type="checkbox"
                                        value={trade.id}
                                        checked={company?.trades?.map((trade) => trade.id).includes(trade.id) || false}
                                        disabled={(tradesAtLimit && !company?.trades?.map((trade) => trade.id).includes(trade.id)) || getTradeIndex(trade.id) === 0}
                                        onChange={(e) => handleTradeCheckboxChange(trade.id, e.target.checked)} // Handle checkbox toggle
                                    />
                                    {trade.name}
                                </label>
                            </div>
                        ))
                    ) : (
                        <p>No trades available</p>
                    )}
                </div>
                <div className="adminServiceCompanyEditFormGroup">
                    <label>Cities:</label>
                    {cities?.length > 0 ? (
                        cities.map((city) => (
                            <div key={city.id}>
                                <label>
                                    <input
                                        type="checkbox"
                                        value={city.id}
                                        checked={company?.cities?.map((city) => city.id).includes(city.id) || false}
                                        onChange={(e) => handleCityCheckboxChange(city.id, e.target.checked)}
                                    />
                                    {city.name}
                                </label>
                            </div>
                        ))
                    ) : (
                        <p>No cities available</p>
                    )}
                </div>
                {company?.original && (
                    <div className="adminServiceCompanyEditFormGroup">
                        <label>Parent:</label>
                        <div>
                            <a href={`/admin/service-company/${company?.original?.id}`}><AdminServiceCompanyRelative
                                company={company?.original}/></a>
                        </div>

                    </div>
                )}
                {company?.children?.length > 0 && (
                    <div className="adminServiceCompanyEditFormGroup">
                        <label>Children:</label>
                        {company.children.map((child) => (
                            <div key={child.id}>
                                <a href={`/admin/service-company/${child.id}`}><AdminServiceCompanyRelative
                                    company={child}/></a>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    type="submit"
                    className={`adminServiceCompanyEditSubmitButton ${
                        buttonState === 'Save Changes' ? 'active' : 'inactive'
                    }`}
                    disabled={buttonState === 'No Changes' || isSubmitting}
                >
                    {buttonState === 'Saving'
                        ? 'Saving...'
                        : buttonState}
                </button>
            </form>
        </div>
    );
}

export default AdminServiceCompanyEdit;
